import React, { useState } from 'react';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import Switch from 'react-switch';

import { addPaymentMethod } from '../../../core/requests/requests';
import { PlusAddIcon, PlusIcon, StripeIcon } from '../../../assets';
import { AddPaymentMethodButtonProps } from './paymentsModels';
import { InfoModal } from '../../../components';
import { useAppData } from '../../../context/AppContext';

const AddPaymentMethodButton: React.FC<AddPaymentMethodButtonProps> = ({
  fetchPaymentMethods,
}) => {
  const { setShowModalBackground, showModalBackground } = useAppData();
  const stripe = useStripe();
  const elements = useElements();
  const [showCardForm, setShowCardForm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentMethodByDefault, setPaymentMethodByDefault] =
    useState<boolean>(true);
  const [paymentMethodName, setPaymentMethodName] = useState<string>('');

  const handleSetPaymentMethodByDefault = (nextChecked: boolean) => {
    setPaymentMethodByDefault(nextChecked);
  };

  const handleChangePaymentMethodName = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPaymentMethodName(e.target.value);
  };
  const handleShowCardForm = () => {
    setShowCardForm(true);
  };
  const handleCloseInfoModal = () => {
    setErrorMessage('');
    setShowModalBackground(false);
  };

  const handleShowInfoModal = (message: string) => {
    setShowModalBackground(true);
    setErrorMessage(message);
  };

  const handleAddCard = async () => {
    if (!stripe || !elements) {
      return;
    }
    setIsProcessing(true);
    try {
      const cardNumberElement = elements.getElement(CardNumberElement);

      if (!cardNumberElement) {
        throw new Error('CardElement not found');
      }

      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumberElement,
      });

      if (error) {
        handleShowInfoModal('Error creating payment method');
        setIsProcessing(false);
        return;
      }

      if (paymentMethod) {
        const response = await addPaymentMethod(
          paymentMethod.id,
          paymentMethodByDefault,
          paymentMethodName
        );
        if (response?.status === 200) {
          fetchPaymentMethods();
          setShowCardForm(false);
          setIsProcessing(false);
          handleShowInfoModal('Payment method added successfully');
          setTimeout(() => {
            handleCloseInfoModal();
          }, 1500);
        }
      }
    } catch (err) {
      handleShowInfoModal('Error adding payment method');
      setIsProcessing(false);
    }
  };

  return (
    <>
      <div className="d-flex-start-start w-100">
        {showCardForm ? (
          <div className="add-payment-method">
            <div className="d-flex-start-start pb-1rem">
              <StripeIcon className="h-2rem" />
            </div>
            <div className="d-flex-row-space-between w-100 pb-1_5rem">
              <div className="d-flex-start-start w-calc-100_minus_10rem">
                <label className="payment-details-input-label">
                  Payment method name
                </label>
                <input
                  placeholder="payment method name"
                  className="payment-details-input w-100"
                  onChange={handleChangePaymentMethodName}
                />
              </div>
              <div className="d-flex-row">
                <span className="mr-0_5rem ff-inter c-6b7280 fw-500 fs-0_875rem">
                  Set as default
                </span>
                <Switch
                  onChange={handleSetPaymentMethodByDefault}
                  checked={paymentMethodByDefault}
                  offColor="#E5E7EB"
                  onColor="#1E75FF"
                  checkedIcon={false}
                  uncheckedIcon={false}
                  height={20}
                  width={40}
                  handleDiameter={16}
                />
              </div>
            </div>
            <div className="d-flex-row-space-between w-100">
              <div className="d-flex-start-start w-calc-100_minus_13rem mr-1rem">
                <label className="payment-details-input-label">
                  Card number
                </label>
                <CardNumberElement className="payment-details-container mb-1rem pt-1rem" />
              </div>
              <div className="d-flex-start-start mr-1rem w-6rem">
                <label className="payment-details-input-label">
                  Expiration date
                </label>
                <CardExpiryElement className="payment-details-container mb-1rem" />
              </div>
              <div className="d-flex-start-start w-4rem">
                <label className="payment-details-input-label">CVC</label>
                <CardCvcElement className="payment-details-container mb-1rem" />
              </div>
            </div>
          </div>
        ) : null}
        {showCardForm ? (
          <button
            className="btn-add-patment-method w-100 mb-1_5rem"
            onClick={handleAddCard}
          >
            <div className="add-patment-method-icon d-flex-center mr-0_5rem">
              {!isProcessing && <PlusIcon />}
            </div>
            <span>
              {isProcessing ? 'Processing...' : 'Save new payment method'}
            </span>
          </button>
        ) : (
          <button
            className="btn-subscriptions-secondary w-100 mb-1_5rem"
            onClick={handleShowCardForm}
          >
            <div className="d-flex-center mr-0_5rem">
              <PlusAddIcon />
            </div>
            <span>Add new payment method</span>
          </button>
        )}
      </div>
      {errorMessage && (
        <InfoModal title={errorMessage} onClose={handleCloseInfoModal} />
      )}
    </>
  );
};

export default AddPaymentMethodButton;
